import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  WhatsappGroup,
  WhatsappMessage,
  watchGroups,
  watchGroupMessages,
  sendGroupMessage,
  sendGroupMedia,
  sendGroupReaction,
  removeGroupReaction,
  deleteGroupMessage,
  uploadWhatsappGroupMedia,
  leaveGroup,
  resetGroupUnread,
} from "../model/whatsapp.model";

const MAX_ATTACHMENT_MB = 20;

// Estado/lógica do painel de grupos, separado da JSX de propósito — o
// pedido do usuário foi manter a barra de abas dentro do card da barra
// lateral (junto com a busca), igual às abas do 1:1. Isso significa que o
// conteúdo de grupo (linhas da lista + detalhe) precisa se encaixar DENTRO
// dos mesmos Cards que o 1:1 usa, em vez de um painel auto-contido com seus
// próprios Cards — daí a extração pra hook, consumido por GroupListRows e
// GroupDetailPanel (components/whatsappGroups/), montados condicionalmente
// dentro da estrutura já existente de Whatsapp.page.tsx.
export default function useWhatsappGroups(
  companyId: string,
  myId: string,
  extensions: { value: string; name: string }[],
) {
  const [groups, setGroups] = useState<WhatsappGroup[]>([]);
  // Lista vazia/ausente em accessUserIds = grupo aberto pra empresa
  // inteira (ver WhatsappGroupModel no backend) — só filtra de verdade
  // quando alguém foi adicionado explicitamente.
  const visibleGroups = groups.filter(
    (g) => !g.accessUserIds || g.accessUserIds.length === 0 || g.accessUserIds.includes(myId),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [messages, setMessages] = useState<WhatsappMessage[]>([]);

  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // "@" sendo digitado agora (texto depois do "@", pra filtrar a lista) —
  // null quando não tem trigger ativo. mentionTriggerStartRef guarda a
  // posição do "@" em si, pra saber o que substituir ao confirmar.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const mentionTriggerStartRef = useRef<number | null>(null);
  // Menções já inseridas no rascunho atual — guarda telefone E nome, porque
  // o composer mostra "@Nome" pro atendente (mais legível que o telefone
  // cru), mas a Z-API exige "@{telefone}" embutido no texto de verdade (ver
  // onSend, que faz essa troca só na hora de mandar). Reconferido contra o
  // texto no envio, pra não mandar uma menção cujo "@Nome" o atendente
  // apagou/alterou manualmente depois de inserir.
  const [mentions, setMentions] = useState<{ phone: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState<WhatsappMessage | null>(null);
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null);
  const [deleteConfirmFor, setDeleteConfirmFor] = useState<WhatsappMessage | null>(null);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordCancelledRef = useRef(false);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  useEffect(() => {
    return watchGroups(companyId, setGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const selected = visibleGroups.find((g) => g.id === selectedId) || null;

  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return;
    }
    setReplyTo(null);
    setMentionQuery(null);
    mentionTriggerStartRef.current = null;
    setMentions([]);
    shouldStickToBottomRef.current = true;
    // Abrir o grupo já zera o não-lido dele pra mim — diferente do 1:1
    // (que só zera ao responder), porque aqui não tem "dono"/fila; é mais
    // parecido com abrir uma conversa de chat normal.
    resetGroupUnread({ groupId: selected.id });
    return watchGroupMessages(companyId, selected.id, setMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selected?.id]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || !shouldStickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 80;
  };

  const rows = visibleGroups
    .filter((g) => !search || g.name.toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => b.lastMessageAt - a.lastMessageAt);

  const insertEmoji = (emoji: string) => {
    const el = composerRef.current;
    const start = el?.selectionStart ?? composerText.length;
    const end = el?.selectionEnd ?? composerText.length;
    const next = composerText.slice(0, start) + emoji + composerText.slice(end);
    setComposerText(next);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + emoji.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  // Chamado a cada tecla digitada no composer (em vez de só setComposerText
  // direto) — detecta se o "@" imediatamente antes do cursor, sem espaço no
  // meio, ainda está "aberto" (sendo digitado agora), pra abrir/filtrar o
  // menu de menção. "@" no meio de uma palavra já escrita (ex: um e-mail)
  // não dispara, porque exige espaço/início de linha antes do "@".
  const onComposerChange = (value: string, cursorPos: number) => {
    setComposerText(value);
    const upToCursor = value.slice(0, cursorPos);
    const match = upToCursor.match(/(?:^|\s)@([^\s@]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      mentionTriggerStartRef.current = cursorPos - match[1].length - 1;
    } else {
      setMentionQuery(null);
      mentionTriggerStartRef.current = null;
    }
  };

  const closeMentionMenu = () => {
    setMentionQuery(null);
    mentionTriggerStartRef.current = null;
  };

  // Substitui o "@parcial" que disparou o menu pelo "@Nome" completo — nome
  // em vez de telefone só pra leitura do atendente no composer (a troca de
  // volta pro telefone que a Z-API exige acontece em onSend). Mesmo
  // mecanismo de posicionar cursor do insertEmoji acima.
  const insertMention = (phone: string, name: string) => {
    const el = composerRef.current;
    const start = mentionTriggerStartRef.current;
    if (start == null) return;
    const cursor = el?.selectionStart ?? composerText.length;
    const mentionText = `@${name} `;
    const next = composerText.slice(0, start) + mentionText + composerText.slice(cursor);
    setComposerText(next);
    setMentions((prev) => (prev.some((m) => m.phone === phone) ? prev : [...prev, { phone, name }]));
    closeMentionMenu();
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + mentionText.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  // Mesmo mecanismo do 1:1 (ver Whatsapp.page.tsx) — cresce verticalmente
  // até um teto, depois rola por dentro.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [composerText]);

  const onSend = async () => {
    if (!selected || !composerText.trim() || sending) return;
    setSending(true);
    const replyToMessageId = replyTo?.id;

    // Composer mostra "@Nome" pro atendente, mas a Z-API exige "@{telefone}"
    // embutido no texto pra funcionar como menção de verdade — troca cada
    // "@Nome" ainda presente pelo "@telefone" correspondente aqui, só na
    // hora de mandar. Se o atendente apagou/alterou o nome manualmente, o
    // token não é mais encontrado e deixa de contar como menção. Ordenado
    // do nome mais longo pro mais curto antes de trocar, pra um nome que é
    // prefixo de outro (ex: "Maria" dentro de "Maria Silva") não roubar a
    // troca de quem contém ele.
    let text = composerText.trim();
    const activeMentions: string[] = [];
    for (const m of [...mentions].sort((a, b) => b.name.length - a.name.length)) {
      const token = `@${m.name}`;
      if (text.includes(token)) {
        text = text.split(token).join(`@${m.phone}`);
        activeMentions.push(m.phone);
      }
    }
    setComposerText("");
    setReplyTo(null);
    setMentions([]);
    const result = await sendGroupMessage({
      groupId: selected.id,
      text,
      replyToMessageId,
      mentions: activeMentions.length > 0 ? activeMentions : undefined,
    });
    if (result.error) toast.error("Não foi possível enviar a mensagem.");
    setSending(false);
  };

  const onReact = async (m: WhatsappMessage, emoji: string) => {
    if (!selected) return;
    setReactionPickerFor(null);
    const result =
      m.reactions?.out === emoji
        ? await removeGroupReaction({ groupId: selected.id, messageId: m.id })
        : await sendGroupReaction({ groupId: selected.id, messageId: m.id, emoji });
    if (result.error) toast.error("Não foi possível reagir à mensagem.");
  };

  const onDeleteMessage = async () => {
    if (!selected || !deleteConfirmFor || deletingMessage) return;
    setDeletingMessage(true);
    try {
      const result = await deleteGroupMessage({
        groupId: selected.id,
        messageId: deleteConfirmFor.id,
      });
      if (result.error) toast.error("Não foi possível apagar a mensagem.");
    } finally {
      setDeletingMessage(false);
      setDeleteConfirmFor(null);
    }
  };

  const onAttachFile = async (file: File) => {
    if (!selected || uploading) return;
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      toast.error(`Arquivo maior que ${MAX_ATTACHMENT_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadWhatsappGroupMedia(companyId, selected.id, file);
      const result = await sendGroupMedia({ groupId: selected.id, ...uploaded });
      if (result.error) toast.error("Não foi possível enviar o anexo.");
    } catch (e) {
      toast.error("Falha ao enviar o arquivo.");
    }
    setUploading(false);
  };

  const startRecording = async () => {
    if (!selected || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
      ].find((c) => window.MediaRecorder?.isTypeSupported?.(c));
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recordedChunksRef.current = [];
      recordCancelledRef.current = false;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!recordCancelledRef.current && recordedChunksRef.current.length > 0) {
          const type = recorder.mimeType || "audio/webm";
          const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
          const blob = new Blob(recordedChunksRef.current, { type });
          onAttachFile(new File([blob], `audio-${Date.now()}.${ext}`, { type }));
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stopRecording = (cancel: boolean) => {
    recordCancelledRef.current = cancel;
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
  };

  // Modal em vez de window.confirm (pedido do usuário) — a lógica de
  // "seria o último?" fica igual, só que agora só decide QUAL variante do
  // modal mostrar (leaveConfirm.isLastMember), a ação de verdade só roda
  // quando confirmLeaveGroup é chamado a partir do clique no modal.
  const [leaveConfirm, setLeaveConfirm] = useState<{
    groupName: string;
    isLastMember: boolean;
  } | null>(null);
  // Sem isso, o botão "Sair do grupo" no painel continuava clicável
  // enquanto a primeira chamada ainda estava em andamento no backend — o
  // modal fecha na hora (antes do await, ver confirmLeaveGroup), então
  // nada impedia reabrir o fluxo e disparar leaveGroup() duas vezes em
  // paralelo.
  const [leavingGroup, setLeavingGroup] = useState(false);

  const requestLeaveGroup = () => {
    if (!selected || leavingGroup) return;
    // Efetivo = lista explícita, ou TODO MUNDO da empresa quando o grupo
    // está aberto (lista vazia) — mesma regra usada no backend pra achar
    // "sou o último". Calculado aqui só pra decidir QUAL variante do modal
    // mostrar; o backend reconfere o mesmo cálculo de verdade na hora de
    // processar (ver WhatsappGroupService.leaveGroup).
    const effectiveMembers = selected.accessUserIds?.length
      ? selected.accessUserIds
      : extensions.map((e) => e.value);
    const isLastMember = effectiveMembers.filter((id) => id !== myId).length === 0;
    setLeaveConfirm({ groupName: selected.name, isLastMember });
  };

  const cancelLeaveGroup = () => setLeaveConfirm(null);

  const confirmLeaveGroup = async () => {
    if (!selected || leavingGroup) return;
    setLeaveConfirm(null);
    setLeavingGroup(true);
    try {
      const result = await leaveGroup({ groupId: selected.id });
      if (result.error) {
        toast.error("Não foi possível sair do grupo.");
        return;
      }
      setSelectedId(null);
    } finally {
      setLeavingGroup(false);
    }
  };

  return {
    groups: visibleGroups,
    rows,
    search,
    setSearch,
    selectedId,
    setSelectedId,
    selected,
    messages,

    composerText,
    setComposerText,
    sending,
    uploading,
    recording,
    recordSeconds,
    emojiOpen,
    setEmojiOpen,
    replyTo,
    setReplyTo,
    reactionPickerFor,
    setReactionPickerFor,
    deleteConfirmFor,
    setDeleteConfirmFor,
    deletingMessage,
    onDeleteMessage,
    composerRef,
    fileInputRef,
    messagesContainerRef,
    onMessagesScroll,
    insertEmoji,
    mentionQuery,
    onComposerChange,
    insertMention,
    closeMentionMenu,
    onSend,
    onReact,
    onAttachFile,
    startRecording,
    stopRecording,
    leaveConfirm,
    leavingGroup,
    requestLeaveGroup,
    cancelLeaveGroup,
    confirmLeaveGroup,

    showCreateModal,
    setShowCreateModal,
    showManageModal,
    setShowManageModal,
  };
}

export type WhatsappGroupsHook = ReturnType<typeof useWhatsappGroups>;
